import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styles from "../styles/pages/Leads.module.css";
import LeadCard from "../components/leads/LeadCard";
import LeadsToolbar from "../components/leads/LeadsToolbar";
import LeadInfoModal from "../components/leads/LeadInfo";
import { useLeadContext } from "../contexts/LeadContext";
import { useAuth } from "../contexts/AuthContext";
import useWhatsAppEnabled from "../hooks/useWhatsAppEnabled";
import { sortAccountsByName } from "../utils/accountOptions";
import { useIncomingNotifications } from "../contexts/NotificationsContext";

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { ReactComponent as AddIcon } from "../assets/icons/add.svg";
import { leadsService } from "../services/leads.service";
import LoadingScreen from "../ui/loadingScreen/LoadingScreen";
import { useLeadSelection } from "../hooks/useLeadSelection";
import BatchCommunicationModal from "../components/leads/BatchCommunicationModal";

function getStatusTitle(status) {
  const percent = status?.order != null ? `${status.order}%` : "";
  const name = status?.name ?? "";

  const match = name.match(/^(\d+%)\s*(.*)$/);

  return {
    percent,
    title: match ? match[2] : name,
  };
}

const readWhatsAppThreadId = (search) => {
  const threadId = Number(new URLSearchParams(search || "").get("whatsapp_thread"));
  return Number.isInteger(threadId) && threadId > 0 ? threadId : null;
};

const Leads = () => {
  const { statuses, responsible, leadUnreadStatus, leadReminderStatus } =
    useLeadContext();
  const { accounts: unsortedAccounts } = useAuth();
  const whatsappEnabled = useWhatsAppEnabled();
  const accounts = useMemo(
    () => sortAccountsByName(unsortedAccounts),
    [unsortedAccounts],
  );
  const subscribeToIncomingNotifications = useIncomingNotifications();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    selectedCount,
    selectedLeads,
    selectedLeadIds,
    lockedAccountId,
    lockedAccountName,
    isSelected,
    canSelectLead,
    toggleLead,
    clearSelection,
    removeLead,
  } = useLeadSelection();

  const [leads, setLeads] = useState([]);
  const [visibleLeads, setVisibleLeads] = useState([]);
  const [booting, setBooting] = useState(true);
  const [baseFilter, setBaseFilter] = useState("my");
  const [accountIds, setAccountIds] = useState([]);
  const [theme, setTheme] = useState(getCurrentTheme);
  const [showModal, setShowModal] = useState(false);
  const [modalLead, setModalLead] = useState(null);
  const [modalMode, setModalMode] = useState("edit");
  const [initialSubjectGroupId, setInitialSubjectGroupId] = useState(null);
  const [initialWhatsAppThreadId, setInitialWhatsAppThreadId] = useState(null);

  const cacheRef = useRef({ my: null });
  const openedLeadIdRef = useRef(null);
  const didInitRef = useRef(false);
  const didSelectAccountRef = useRef(false);
  const loadGenerationRef = useRef(0);

  function getCurrentTheme() {
    return document.documentElement.getAttribute("data-theme") === "dark"
      ? "dark"
      : "light";
  }

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(getCurrentTheme());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  const loadLeads = useCallback(async (accountId) => {
    loadGenerationRef.current += 1;
    const generation = loadGenerationRef.current;
    setBooting(true);
    try {
      const mine = await leadsService.myLeads(
        accountId ? { accountId } : {},
      );
      if (generation !== loadGenerationRef.current) return;

      cacheRef.current.my = mine;
      setLeads(mine);
      setVisibleLeads(mine);
      setBaseFilter("my");
    } catch (err) {
      if (generation !== loadGenerationRef.current) return;
      console.error("Failed to fetch leads:", err?.response?.data || err);
      cacheRef.current.my = [];
      setLeads([]);
      setVisibleLeads([]);
    } finally {
      if (generation === loadGenerationRef.current) setBooting(false);
    }
  }, []);

  // Carga inicial inmediata al montar (no espera a la sesión, para no demorar
  // la pantalla). Si las cuentas ya están disponibles, arranca con la primera.
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    const firstAccountId = accounts?.[0]?.id ?? null;
    if (firstAccountId) {
      didSelectAccountRef.current = true;
      setAccountIds([firstAccountId]);
    }
    loadLeads(firstAccountId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuando las cuentas terminan de cargar (si no estaban al montar),
  // seleccionamos la primera y traemos sus leads (server-side). Una sola vez.
  useEffect(() => {
    if (didSelectAccountRef.current) return;
    if (!accounts?.length) return;

    didSelectAccountRef.current = true;
    const firstAccountId = accounts[0].id;
    setAccountIds([firstAccountId]);
    loadLeads(firstAccountId);
  }, [accounts, loadLeads]);

  const activeStatuses = useMemo(() => {
    return statuses
      .filter((s) => s.is_active)
      .sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return a.id - b.id;
      });
  }, [statuses]);

  const handleDueReminderChange = useCallback((leadId, hasDueReminder) => {
    const applyDueReminder = (list) => {
      if (!Array.isArray(list)) return list;

      let changed = false;
      const next = list.map((lead) => {
        if (lead.id !== leadId) return lead;
        if (Boolean(lead.has_due_reminder) === hasDueReminder) return lead;

        changed = true;
        return { ...lead, has_due_reminder: hasDueReminder };
      });

      return changed ? next : list;
    };

    setLeads(applyDueReminder);
    setVisibleLeads(applyDueReminder);
    cacheRef.current.my = applyDueReminder(cacheRef.current.my);
  }, []);

  useEffect(() => {
    if (!subscribeToIncomingNotifications) return undefined;

    return subscribeToIncomingNotifications((notification) => {
      const notificationCode = notification?.notification_type?.code;

      if (notificationCode === "follow_up_reminder") {
        const reminderLeadId = notification?.payload?.lead_id;
        if (reminderLeadId != null) {
          handleDueReminderChange(reminderLeadId, true);
        }
        return;
      }

      if (notificationCode !== "lead_status_changed") {
        return;
      }

      const payload = notification.payload || {};
      const leadId = payload.lead_id;
      const newStatusId = payload.new_status?.id;
      if (leadId == null || newStatusId == null) return;

      const statusObj =
        statuses.find((s) => s.id === newStatusId) || payload.new_status;

      const applyStatus = (list) =>
        list.map((lead) =>
          lead.id === leadId ? { ...lead, status: statusObj } : lead,
        );

      setLeads(applyStatus);
      setVisibleLeads(applyStatus);

      if (Array.isArray(cacheRef.current.my)) {
        cacheRef.current.my = applyStatus(cacheRef.current.my);
      }

      setModalLead((curr) =>
        curr?.id === leadId ? { ...curr, status: statusObj } : curr,
      );
    });
  }, [subscribeToIncomingNotifications, statuses, handleDueReminderChange]);

  const mailAccounts = useMemo(() => {
    const byId = new Map();

    selectedLeads.forEach((lead) => {
      const leadResponsible = lead?.responsible;
      if (!leadResponsible?.id || byId.has(leadResponsible.id)) return;

      const email =
        leadResponsible.email ??
        (responsible || []).find((corp) => corp.id === leadResponsible.id)
          ?.email ??
        "";

      byId.set(leadResponsible.id, { id: leadResponsible.id, email });
    });

    return Array.from(byId.values()).filter((account) => account.email);
  }, [selectedLeads, responsible]);

  const handleFilterChange = useCallback(
    async (newFilter, data) => {
      if (newFilter === "account") {
        const ids = Array.isArray(data) ? data.filter(Boolean) : [];
        setAccountIds(ids);
        // Server-side: volvemos a pedir los leads filtrados por la cuenta.
        loadLeads(ids[0] ?? null);
        return;
      }

      if (newFilter === "my") {
        setBaseFilter("my");
        setVisibleLeads(cacheRef.current.my ?? []);
      } else if (newFilter === "custom") {
        const { inputValue, selectedType, selectedUser, visibility } = data;
        const q = (inputValue || "").toLowerCase();

        const selectedMailAccountId = selectedUser?.id ?? null;

        const filtered = (cacheRef.current.my ?? []).filter((lead) => {
          const matchesSearch =
            (lead.lead_name || "").toLowerCase().includes(q) ||
            (lead.first_name || "").toLowerCase().includes(q) ||
            (lead.last_name || "").toLowerCase().includes(q);

          const matchesType = selectedType
            ? lead.type?.id === selectedType.id
            : true;

          const matchesUser =
            selectedMailAccountId != null
              ? lead.responsible?.id === selectedMailAccountId
              : true;

          const matchesVisibility =
            visibility === "visible"
              ? lead.visible === true
              : visibility === "not_visible"
                ? lead.visible === false
                : true;

          return (
            matchesSearch && matchesType && matchesUser && matchesVisibility
          );
        });

        setVisibleLeads(filtered);
        setBaseFilter("custom");
      }
    },
    [loadLeads],
  );

  const handleNewLead = () => {
    openedLeadIdRef.current = null;
    setModalLead(null);
    setModalMode("create");
    setShowModal(true);
    navigate("/leads");
  };

  const handleOpenLead = useCallback(
    (lead) => {
      const fresh = leads.find((l) => l.id === lead.id) ?? lead;
      openedLeadIdRef.current = fresh.id;
      setInitialSubjectGroupId(null);
      setInitialWhatsAppThreadId(null);
      setModalLead(fresh);
      setModalMode("edit");
      setShowModal(true);
      navigate(`/leads/${fresh.id}`);
    },
    [leads, navigate],
  );

  const handleUnreadMailChange = useCallback((leadId, hasUnread) => {
    const applyUnread = (list) => {
      if (!Array.isArray(list)) return list;

      let changed = false;
      const next = list.map((lead) => {
        if (lead.id !== leadId) return lead;
        if (Boolean(lead.has_unread_mail) === hasUnread) return lead;

        changed = true;
        return { ...lead, has_unread_mail: hasUnread };
      });

      return changed ? next : list;
    };

    setLeads(applyUnread);
    setVisibleLeads(applyUnread);
    cacheRef.current.my = applyUnread(cacheRef.current.my);
  }, []);

  useEffect(() => {
    if (!leadUnreadStatus?.leadId) return;

    handleUnreadMailChange(leadUnreadStatus.leadId, leadUnreadStatus.hasUnread);
  }, [leadUnreadStatus, handleUnreadMailChange]);

  useEffect(() => {
    if (!leadReminderStatus?.leadId) return;

    handleDueReminderChange(
      leadReminderStatus.leadId,
      leadReminderStatus.hasDueReminder,
    );
  }, [leadReminderStatus, handleDueReminderChange]);

  const handleCloseModal = useCallback(() => {
    openedLeadIdRef.current = null;
    setShowModal(false);
    setModalLead(null);
    setModalMode("edit");
    setInitialSubjectGroupId(null);
    setInitialWhatsAppThreadId(null);
    navigate("/leads");
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    const openLeadFromUrl = async () => {
      if (!id) {
        openedLeadIdRef.current = null;
        return;
      }

      const leadId = Number(id);
      if (Number.isNaN(leadId)) return;
      if (openedLeadIdRef.current === leadId) {
        setInitialWhatsAppThreadId(whatsappEnabled ? readWhatsAppThreadId(location.search) : null);
        return;
      }

      let lead = null;
      try {
        lead = await leadsService.getLeadDetail({ leadId });
      } catch (requestError) {
        console.error("No se pudo obtener el lead por id:", requestError);
      }

      if (!lead) {
        lead = leads.find((leadInList) => leadInList.id === leadId) ?? null;
      }

      if (!cancelled && lead) {
        openedLeadIdRef.current = leadId;
        setInitialSubjectGroupId(location.state?.openSubjectGroupId ?? null);
        setInitialWhatsAppThreadId(whatsappEnabled ? readWhatsAppThreadId(location.search) : null);
        setModalLead(lead);
        setModalMode("edit");
        setShowModal(true);
      }
    };

    openLeadFromUrl();

    return () => {
      cancelled = true;
    };
  }, [id, leads, location.state, location.search, whatsappEnabled]);

  const handleDeleted = useCallback(
    (leadId) => {
      const withoutLead = (list) => (list ?? []).filter((lead) => lead.id !== leadId);
      cacheRef.current.my = withoutLead(cacheRef.current.my);
      setLeads(withoutLead);
      setVisibleLeads(withoutLead);
      removeLead(leadId);
    },
    [removeLead],
  );

  const handleSaved = (resp) => {
    const updated = resp?.data?.data ?? resp?.data ?? resp;
    if (!updated?.id) return;

    setLeads((prev) => {
      const exists = prev.some((l) => l.id === updated.id);
      const next = exists
        ? prev.map((l) => (l.id === updated.id ? updated : l))
        : [updated, ...prev];

      return next;
    });

    setModalLead((prev) => (prev?.id === updated.id ? updated : prev));

    if (Array.isArray(cacheRef.current.my)) {
      const existsInMine = cacheRef.current.my.some((l) => l.id === updated.id);
      cacheRef.current.my = existsInMine
        ? cacheRef.current.my.map((l) => (l.id === updated.id ? updated : l))
        : [updated, ...cacheRef.current.my];
    }

    if (baseFilter === "my") {
      setVisibleLeads(cacheRef.current.my ?? []);
    } else if (baseFilter === "custom") {
      setVisibleLeads((prev) =>
        prev.map((l) => (l.id === updated.id ? updated : l)),
      );
    }
  };

  const uniqueLeads = Array.from(
    new Map(visibleLeads.map((l) => [l.id, l])).values(),
  );

  const sortedStatuses = activeStatuses;
  const columnOrder = sortedStatuses.map((s) => s.id);
  const statusById = Object.fromEntries(sortedStatuses.map((s) => [s.id, s]));

  const leadsByStatus = uniqueLeads.reduce((acc, lead) => {
    const statusId = lead.status?.id || "Sin estado";
    (acc[statusId] ||= []).push(lead);
    return acc;
  }, {});

  Object.values(leadsByStatus).forEach((columnLeads) => {
    columnLeads.sort(
      (first, second) =>
        Number(Boolean(second.has_unread_mail)) -
          Number(Boolean(first.has_unread_mail)) ||
        Number(Boolean(second.has_due_reminder)) -
          Number(Boolean(first.has_due_reminder)),
    );
  });

  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const sourceStatus = Number(source.droppableId);
    const destStatus = Number(destination.droppableId);

    const draggedLead = leadsByStatus[sourceStatus]?.[source.index];
    if (!draggedLead) return;

    const newStatusObj = statuses.find((s) => s.id === destStatus);
    if (!newStatusObj) return;

    const prevLeads = leads;
    const prevVisibleLeads = visibleLeads;
    const prevMyLeads = cacheRef.current.my;

    const optimisticLead = {
      ...draggedLead,
      status: newStatusObj,
    };

    const updateLeadInList = (list) =>
      list.map((lead) => (lead.id === draggedLead.id ? optimisticLead : lead));

    setLeads(updateLeadInList);
    setVisibleLeads(updateLeadInList);

    if (Array.isArray(cacheRef.current.my)) {
      cacheRef.current.my = updateLeadInList(cacheRef.current.my);
    }

    setModalLead((curr) =>
      curr?.id === draggedLead.id ? optimisticLead : curr,
    );

    try {
      const updated = await leadsService.updateStatus({
        clienteId: draggedLead.cliente_id ?? draggedLead.id,
        statusId: destStatus,
      });

      if (updated?.id) {
        const replaceWithUpdated = (list) =>
          list.map((lead) => (lead.id === updated.id ? updated : lead));

        setLeads(replaceWithUpdated);
        setVisibleLeads(replaceWithUpdated);

        if (Array.isArray(cacheRef.current.my)) {
          cacheRef.current.my = replaceWithUpdated(cacheRef.current.my);
        }

        setModalLead((curr) => (curr?.id === updated.id ? updated : curr));
      }
    } catch (err) {
      console.error("Error actualizando status:", err?.response?.data || err);

      setLeads(prevLeads);
      setVisibleLeads(prevVisibleLeads);
      cacheRef.current.my = prevMyLeads;
    }
  };

  return (
    <div className={styles.leads}>
      <div className={styles.leads1}>
        <div className={styles.leadsInner}>
          <div className={styles.frameParent}>
            <div className={styles.leadsWrapper}>
              <div className={styles.div}>Leads</div>
            </div>

            <LeadsToolbar
              onFilterChange={handleFilterChange}
              leads={leads}
              selectedAccountId={accountIds[0] ?? null}
              onNewLead={handleNewLead}
              onOpenLead={handleOpenLead}
            />
          </div>
        </div>

        {booting && <LoadingScreen mainText="Cargando Leads..." embedded />}

        {!booting &&
          (statuses.length === 0 ? (
            <div>Cargando estados...</div>
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <div className={styles.frameContainer}>
                {columnOrder.map((statusId) => (
                  <Droppable droppableId={statusId.toString()} key={statusId}>
                    {(provided) => (
                      <div
                        className={styles.frameDiv}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        <div className={styles.parent}>
                          {(() => {
                            const { percent, title } = getStatusTitle(
                              statusById[statusId],
                            );
                            const columnLeads = leadsByStatus[statusId] || [];

                            return (
                              <>
                                <div className={styles.div}>{percent}</div>
                                <div className={styles.leadTitle}>{title}</div>
                                <div
                                  className={styles.columnCount}
                                  aria-label={`${columnLeads.length} leads`}
                                >
                                  {columnLeads.length}
                                </div>
                              </>
                            );
                          })()}
                        </div>

                        <div
                          className={styles.newLead}
                          onClick={handleNewLead}
                          role="button"
                          tabIndex={0}
                        >
                          <div className={styles.iconsdashboard}>
                            <AddIcon className={styles.vectorIcon} />
                          </div>
                        </div>

                        {(leadsByStatus[statusId] || []).map((lead, idx) => (
                          <Draggable
                            key={lead.id.toString()}
                            draggableId={lead.id.toString()}
                            index={idx}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <LeadCard
                                  {...lead}
                                  theme={theme}
                                  onOpen={() => handleOpenLead(lead)}
                                  selected={isSelected(lead.id)}
                                  selectable={canSelectLead(lead)}
                                  onToggleSelect={() => toggleLead(lead)}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}

                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                ))}
              </div>
            </DragDropContext>
          ))}
      </div>

      {showModal && (
        <LeadInfoModal
          visible={showModal}
          lead={modalLead}
          statuses={activeStatuses}
          onClose={handleCloseModal}
          mode={modalMode}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
          initialSubjectGroupId={initialSubjectGroupId}
          onUnreadMailChange={handleUnreadMailChange}
          initialWhatsAppThreadId={initialWhatsAppThreadId}
        />
      )}

      {selectedCount > 0 && (
        <BatchCommunicationModal
          count={selectedCount}
          leadIds={selectedLeadIds}
          accountId={lockedAccountId}
          accountName={lockedAccountName}
          mailAccounts={mailAccounts}
          onClose={clearSelection}
        />
      )}
    </div>
  );
};

export default Leads;
